import { useMemo, useState, useEffect } from "react";
import { UserPlus, Users } from "lucide-react";
import { formatDate } from "../Utils/dateFormatter";
import Logo from "../../Images/Packky.svg";
import useDriver from "../../Sevices/Driver Api/useDriver";
import useAttendance from "../../Sevices/useAttendance";
import useSettlement from "../../Sevices/useSettlement";
import useReports from "../../Sevices/useReports";
import toast from "react-hot-toast";
import SettlementTab from "./SettlementTab";
import SettlementModals from "./SettlementModals";

const pad2 = (num) => String(num).padStart(2, "0");

const toApiDate = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const formatWeekLabelDate = (date) => {
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
};

const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);

  d.setDate(diff);
  d.setHours(0, 0, 0, 0);

  return d;
};

const buildSettlementWeeks = (year) => {
  const weeks = [];

  let cursor = getMonday(new Date(year, 0, 1));
  let index = 1;

  while (cursor.getFullYear() <= year || weeks.length < 53) {
    const start = new Date(cursor);
    const end = new Date(cursor);

    end.setDate(start.getDate() + 6);

    if (start.getFullYear() > year) break;

    weeks.push({
      id: `${toApiDate(start)}_${toApiDate(end)}`,
      weekNumber: index,
      fromDate: toApiDate(start),
      toDate: toApiDate(end),
      label: `Week ${index} • ${formatWeekLabelDate(start)} - ${formatWeekLabelDate(end)} ${end.getFullYear()}`,
      shortLabel: `Week ${index}`,
      display: `${toApiDate(start)} to ${toApiDate(end)}`,
      searchText:
        `week ${index} ${toApiDate(start)} ${toApiDate(end)} ${formatWeekLabelDate(start)} ${formatWeekLabelDate(end)}`.toLowerCase(),
    });

    cursor.setDate(cursor.getDate() + 7);
    index += 1;

    if (index > 54) break;
  }

  return weeks;
};

const Settlement = () => {
  const {
    settlementRows,
    settlementType,
    settlementLoading,
    fetchSettlementAmount,
  } = useDriver();
  const { fetchAttendanceApi } = useAttendance();
  const {
    fetchDriverWeeklyPerformancePoints,
    weeklyPointsLoading: modalWeeklyPointsLoading,
  } = useReports();

const { updateSettlementAmount, paying: updateSettlementLoading } =
  useSettlement();
  const [activeTab, setActiveTab] = useState("settlement");
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [modalAttendanceData, setModalAttendanceData] = useState([]);
  const [modalAttendanceDirtyMap, setModalAttendanceDirtyMap] = useState({});
  const [modalAttendanceLoading, setModalAttendanceLoading] = useState(false);
  const [editSettlement, setEditSettlement] = useState(null);
  const [editSettlementAmount, setEditSettlementAmount] = useState("");
  const [editPerformanceAmount, setEditPerformanceAmount] = useState("");
const [modalWeeklyPoints, setModalWeeklyPoints] = useState([]);
const [editDate, setEditDate] = useState("");

  const settlementPerPage = 25;
  const [currentPage, setCurrentPage] = useState(1);

  const currentYear = new Date().getFullYear();

  const [settlementYear, setSettlementYear] = useState(currentYear);
  const [settlementWeekSearch, setSettlementWeekSearch] = useState("");
  const [showSettlementWeekDropdown, setShowSettlementWeekDropdown] =
    useState(false);

  const settlementWeekOptions = useMemo(() => {
    return buildSettlementWeeks(settlementYear);
  }, [settlementYear]);

  const todayMonday = getMonday(new Date());
  const todaySunday = new Date(todayMonday);
  todaySunday.setDate(todayMonday.getDate() + 6);

  const defaultSettlementWeekId = `${toApiDate(todayMonday)}_${toApiDate(
    todaySunday
  )}`;

  const [selectedSettlementWeekId, setSelectedSettlementWeekId] = useState(
    defaultSettlementWeekId
  );

  const selectedSettlementWeek = useMemo(() => {
    return (
      settlementWeekOptions.find(
        (week) => week.id === selectedSettlementWeekId
      ) || settlementWeekOptions[0]
    );
  }, [settlementWeekOptions, selectedSettlementWeekId]);

  const filteredSettlementWeekOptions = useMemo(() => {
    const q = settlementWeekSearch.trim().toLowerCase();

    if (!q) return settlementWeekOptions;

    return settlementWeekOptions.filter((week) => week.searchText.includes(q));
  }, [settlementWeekOptions, settlementWeekSearch]);

  const fromDate = selectedSettlementWeek?.fromDate || toApiDate(todayMonday);
  const toDate = selectedSettlementWeek?.toDate || toApiDate(todaySunday);

  const formatAmount = (value) => {
    const amount = Number(value || 0);

    return amount.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    });
  };

  const getStatusMeta = (status) => {
    const normalized = String(status || "").toUpperCase();

    if (normalized === "Y") {
      return {
        label: "Settled",
        className: "bg-emerald-50 text-emerald-700 ring-emerald-200",
      };
    }

    return {
      label: "Pending",
      className: "bg-orange-50 text-orange-700 ring-orange-200",
    };
  };

 const getDateRangeLabel = (createdOn) => {
   if (!createdOn) return "-";

   const [start, end] = String(createdOn).split(":");

   if (!start || !end) return createdOn;

   return `${formatDate(start)} to ${formatDate(end)}`;
 };

  const handleFetchSettlement = () => {
    if (!selectedSettlementWeek) return;

    fetchSettlementAmount({
      fromDate: selectedSettlementWeek.fromDate,
      toDate: selectedSettlementWeek.toDate,
    });
  };

  const handleSettlementWeekSelect = (week) => {
    setSelectedSettlementWeekId(week.id);
    setSettlementWeekSearch("");
    setShowSettlementWeekDropdown(false);
    setCurrentPage(1);

    fetchSettlementAmount({
      fromDate: week.fromDate,
      toDate: week.toDate,
    });
  };

  useEffect(() => {
    if (activeTab === "settlement" && selectedSettlementWeek) {
      fetchSettlementAmount({
        fromDate: selectedSettlementWeek.fromDate,
        toDate: selectedSettlementWeek.toDate,
        silent: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    const closeDropdown = (e) => {
      if (!e.target.closest?.("[data-settlement-week-picker]")) {
        setShowSettlementWeekDropdown(false);
      }
    };

    document.addEventListener("mousedown", closeDropdown);

    return () => {
      document.removeEventListener("mousedown", closeDropdown);
    };
  }, []);

  const filteredSettlements = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return settlementRows || [];

    return (settlementRows || []).filter((item) => {
      return (
        String(item.driver_code || "").toLowerCase().includes(q) ||
        String(item.name || "").toLowerCase().includes(q) ||
        String(item.bank_name || "").toLowerCase().includes(q) ||
        String(item.bank_holder_name || "").toLowerCase().includes(q) ||
        String(item.account_no || "").toLowerCase().includes(q) ||
        String(item.ifsc_code || "").toLowerCase().includes(q)
      );
    });
  }, [settlementRows, search]);

  const totalSettlementAmount = useMemo(() => {
    return filteredSettlements.reduce(
      (sum, item) => sum + Number(item.total_amount || 0),
      0
    );
  }, [filteredSettlements]);

  const pendingCount = useMemo(() => {
    return filteredSettlements.filter(
      (item) => String(item.status || "").toUpperCase() !== "Y"
    ).length;
  }, [filteredSettlements]);

  const settledCount = useMemo(() => {
    return filteredSettlements.filter(
      (item) => String(item.status || "").toUpperCase() === "Y"
    ).length;
  }, [filteredSettlements]);

  const indexOfLast = currentPage * settlementPerPage;
  const indexOfFirst = indexOfLast - settlementPerPage;

  const currentSettlements = filteredSettlements.slice(
    indexOfFirst,
    indexOfLast
  );

const handleEditSettlement = (item) => {
  setEditSettlement(item);
  setEditSettlementAmount("");
  setEditPerformanceAmount("");

  setEditDate(toDate);
};

const closeEditSettlementModal = () => {
  if (updateSettlementLoading) return;

  setEditSettlement(null);
  setEditSettlementAmount("");
  setEditPerformanceAmount("");
  setEditDate("");
};

  const handleUpdateSettlement = async () => {
    if (!editSettlement?.driver_code) return;

    if (!editDate) {
      toast.error("Please select date");
      return;
    }

    if (
      String(editSettlementAmount).trim() === "" &&
      String(editPerformanceAmount).trim() === ""
    ) {
      toast.error("Enter settlement amount or performance points amount");
      return;
    }

    const result = await updateSettlementAmount({
      date: editDate,
      driverId: editSettlement.driver_code,

      settlementAmount:
        String(editSettlementAmount).trim() === ""
          ? ""
          : String(editSettlementAmount).trim(),

      performmancePointsAmount:
        String(editPerformanceAmount).trim() === ""
          ? "0"
          : String(editPerformanceAmount).trim(),
    });

    if (!result) return;

    closeEditSettlementModal();

    await fetchSettlementAmount({
      fromDate,
      toDate,
    });
  };

const handleViewSettlement = async (item) => {
  setSelectedSettlement(item);
  setModalAttendanceData([]);
  setModalAttendanceDirtyMap({});
  setModalWeeklyPoints([]);

  await Promise.all([
    fetchAttendanceApi(
      {
        driverCode: item.driver_code,
        fromDate,
        toDate,
      },
      setModalAttendanceData,
      setModalAttendanceDirtyMap,
      setModalAttendanceLoading,
    ),

    fetchDriverWeeklyPerformancePoints(
      item.driver_code,
      fromDate,
      toDate,
      setModalWeeklyPoints,
    ),
  ]);
};
  console.log("TABLE DEBUG", {
  settlementRowsLength: settlementRows?.length,
  filteredLength: filteredSettlements?.length,
  currentLength: currentSettlements?.length,
  settlementLoading,
  currentPage,
});

  return (
    <>
      <div className="flex h-full min-h-0 flex-col overflow-hidden p-6">
        {/* Header */}
        <div className=" shkink-0 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center">
            <img src={Logo} alt="Logo" className="h-10" />
            <p className="ps-3 text-2xl font-bold">Settlement Report</p>
          </div>

          {activeTab === "employee" && (
            <button
              onClick={() => setEmployeeModalOpen(true)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600"
            >
              <UserPlus size={17} />
              Add Employee
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="mb-5 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab("settlement")}
              className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition ${
                activeTab === "settlement"
                  ? "border-orange-500 text-orange-500"
                  : "border-transparent text-gray-600 hover:text-orange-500"
              }`}
            >
              Settlement
            </button>

            <button
              onClick={() => setActiveTab("employee")}
              className={`inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition ${
                activeTab === "employee"
                  ? "border-orange-500 text-orange-500"
                  : "border-transparent text-gray-600 hover:text-orange-500"
              }`}
            >
              <Users size={16} />
              Employee List
            </button>
          </nav>
        </div>

        {activeTab === "settlement" && (
          <SettlementTab
            settlementType={settlementType}
            settlementYear={settlementYear}
            setSettlementYear={setSettlementYear}
            currentYear={currentYear}
            buildSettlementWeeks={buildSettlementWeeks}
            settlementWeekSearch={settlementWeekSearch}
            setSettlementWeekSearch={setSettlementWeekSearch}
            showSettlementWeekDropdown={showSettlementWeekDropdown}
            setShowSettlementWeekDropdown={setShowSettlementWeekDropdown}
            selectedSettlementWeek={selectedSettlementWeek}
            selectedSettlementWeekId={selectedSettlementWeekId}
            setSelectedSettlementWeekId={setSelectedSettlementWeekId}
            filteredSettlementWeekOptions={filteredSettlementWeekOptions}
            handleSettlementWeekSelect={handleSettlementWeekSelect}
            search={search}
            setSearch={setSearch}
            settlementLoading={settlementLoading}
            fetchSettlementAmount={fetchSettlementAmount}
            handleFetchSettlement={handleFetchSettlement}
            fromDate={fromDate}
            toDate={toDate}
            totalSettlementAmount={totalSettlementAmount}
            pendingCount={pendingCount}
            settledCount={settledCount}
            filteredSettlements={filteredSettlements}
            currentSettlements={currentSettlements}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            settlementPerPage={settlementPerPage}
            indexOfFirst={indexOfFirst}
            formatAmount={formatAmount}
            getStatusMeta={getStatusMeta}
            getDateRangeLabel={getDateRangeLabel}
            handleViewSettlement={handleViewSettlement}
          />
        )}

        {activeTab === "employee" && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-white shadow-sm">
            <div className="shrink-0 border-b bg-orange-50 px-4 py-3">
              <p className="text-sm font-semibold text-orange-700">
                Employee List
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              <iframe
                title="Employee List"
                src="https://billpunch.com/widget/packkyemployee/"
                className="block h-full min-h-[700px] w-full border-0"
              />
            </div>
          </div>
        )}
      </div>

      <SettlementModals
        selectedSettlement={selectedSettlement}
        setSelectedSettlement={setSelectedSettlement}
        modalAttendanceData={modalAttendanceData}
        modalAttendanceLoading={modalAttendanceLoading}
        modalWeeklyPoints={modalWeeklyPoints}
        modalWeeklyPointsLoading={modalWeeklyPointsLoading}
        fromDate={fromDate}
        toDate={toDate}
        formatAmount={formatAmount}
        getStatusMeta={getStatusMeta}
        editSettlement={editSettlement}
        editSettlementAmount={editSettlementAmount}
        setEditSettlementAmount={setEditSettlementAmount}
        editPerformanceAmount={editPerformanceAmount}
        setEditPerformanceAmount={setEditPerformanceAmount}
        editDate={editDate}
        setEditDate={setEditDate}
        closeEditSettlementModal={closeEditSettlementModal}
        handleUpdateSettlement={handleUpdateSettlement}
        updateSettlementLoading={updateSettlementLoading}
        employeeModalOpen={employeeModalOpen}
        setEmployeeModalOpen={setEmployeeModalOpen}
      />
    </>
  );
};

export default Settlement;